import Container from '@components/Container'
import SEO from '@components/SEO'
import StaffComponent from '@components/StaffComponent'
import { StaffWrap } from '@components/StaffWrap'
import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { FlexIndexQuery } from '@graphql-types'
import { graphql, PageProps } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import GoogleMapReact from 'google-map-react'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
	FaVideo,
	FaChalkboardTeacher,
	FaCode,
	FaPlayCircle,
	FaWhatsapp,
} from 'react-icons/fa'
import { MdLanguage } from 'react-icons/md'
import { maxWidth } from 'src/themes'
import { lighten, desaturate } from 'polished'
import { ButtonLink } from '@components/ButtonLink'
import { ButtonA } from '@components/ButtonA'

const Icon = styled.div`
	span {
		display: flex;
		background: ${(props) => props.theme.buttonColor};
		font-size: 40px;
		width: 60px;
		height: 60px;
		justify-content: center;
		align-items: center;
		border-radius: 30px;
		margin-bottom: 10px;
	}
`

const FlexSchedule = styled.div`
	display: flex;
	justify-content: center;
	align-items: stretch;
	flex-wrap: wrap;

	> div {
		flex: 1;
		padding: 20px;
		margin: 10px;
		min-width: 49%;
		border: solid 1px #ffffff;
		display: flex;
		align-items: center;

		> .content {
			flex: 1;

			> span {
				display: flex;
				align-items: center;
				gap: 0.5rem;
			}
		}

		> .price {
			text-align: right;

			.currency {
				font-size: 0.7em;
				margin-right: 0.2em;
			}

			p {
				font-size: 2.5rem;
				font-weight: bold;
			}

			em {
				font-size: 1rem;
				display: block;
			}

			a {
				margin-top: 0.4rem;
				padding: 0.2rem 1rem;
			}
		}
	}

	p {
		margin: 0;
	}

	.month {
		color: #ffcc00;
		font-size: 24px;
		font-weight: bold;
	}

	${maxWidth[2]} {
		flex-direction: column;
	}
`

const FlexSection = styled.div`
	flex: 1;
	padding: 1rem;
	margin: 0.5rem;

	.icon {
		display: flex;

		span {
			margin-right: 1rem;
		}
	}

	&:first-of-type {
		background: ${(props) =>
			lighten(0.4)(desaturate(0.4, props.theme.accentColor))};
	}
	&:nth-child(2) {
		background: ${(props) =>
			lighten(0.2)(desaturate(0.3, props.theme.accentColor))};
	}
	&:last-child {
		background: ${(props) =>
			lighten(0)(desaturate(0.2, props.theme.accentColor))};
	}

	.video {
		display: flex;
		font-size: 0.8rem;
		background: #ffffff;
		color: ${(props) => props.theme.accentColor};
		padding: 0.2rem 0.4rem;
		margin: 0.2rem 0;
		span {
			font-size: 0.6rem;
			display: block;
			flex: 1;
			text-align: right;
		}
	}

	.practice {
		background: #ffffff;
		color: ${(props) => props.theme.accentColor};
		padding: 0.2rem 0.4rem;
		margin: 0.2rem 0;

		p {
			margin: 0;

			&:last-child {
				font-size: 0.8rem;
				margin-top: 0.5rem;
			}
		}
	}

	.practice {
		padding: 0.2rem 0.4rem;
		margin: 0.2rem 0;
		font-size: 0.8rem;
	}

	.codes {
		border-bottom: solid 1px #ffffff;
		font-size: 0.8rem;
		margin: 0.2rem 0;
	}
`

const FlexContainer = styled.div`
	padding: 1px;

	> div {
		padding: 20px 80px 20px;
		${maxWidth[2]} {
			padding: 20px 10px 20px;
		}
	}

	h3 {
		border: none;
		font-size: 50px;
		margin: 20px 0;
	}

	h4 {
		font-size: 35px;
		margin: 20px 0;
	}

	h5 {
		color: #ffcc00;
		font-weight: bold;
		font-size: 20px;
	}

	h3,
	h4 {
		text-align: center;
		font-weight: bold;
	}
`

const FlexIndex = ({ data }: PageProps<FlexIndexQuery>) => {
	const { t } = useTranslation()
	const theme = useTheme()

	const course = data.course!
	const cohorts: {
		name: string
		dates: string[]
		venue: string
		endTime: string
		startTime: string
		show: boolean
	}[] = []

	for (const lesson of data.events.edges) {
		if (lesson.node.summary == null) {
			continue
		}
		const [, cohortName] = lesson.node.summary.split(' ', 2)
		let cohort = cohorts.find((aCohort) => aCohort.name === cohortName)
		if (cohort == null) {
			cohort = {
				name: cohortName,
				dates: [
					moment(lesson.node.start?.dateTime).format('D/M (ddd)'),
				],
				venue: lesson.node.location ?? '',
				endTime: moment(lesson.node.end?.dateTime).format('HH:mm'),
				startTime: moment(lesson.node.start?.dateTime).format('HH:mm'),
				show: moment(lesson.node.start?.dateTime).isAfter(new Date()),
			}
			cohorts.push(cohort)
		} else {
			cohort.dates.push(
				moment(lesson.node.start?.dateTime).format('D/M (ddd)')
			)
		}
	}

	return (
		<Container>
			<SEO title={`${course.name} (${course.code})`} />
			<div
				css={css`
					display: flex;
					width: 100%;

					${maxWidth[2]} {
						flex-direction: column;
					}

					> div:first-of-type {
						margin-right: 40px;
					}

					> div:last-child {
						flex: 1;
					}
				`}>
				<div>
					<GatsbyImage
						image={
							course.iconSharp?.childImageSharp?.gatsbyImageData
						}
						alt={`${course.name} (${course.code})`}
					/>
				</div>
				<div>
					<p
						css={css`
							font-family: 'Kaushan Script', cursive;
						`}>
						{t('courses:flex.title')}
					</p>
					<h1>{course.name}</h1>
					<h2>{course.tagline}</h2>
					<div
						css={css`
							display: flex;
							> div {
								margin-right: 30px;

								${maxWidth[2]} {
									margin-right: 10px;
								}
								span {
									font-size: 40px;
									width: 60px;
									height: 60px;
									border-radius: 30px;
									margin-bottom: 10px;
								}
							}
						`}>
						<Icon>
							<span>
								<FaVideo />
							</span>
							<p>
								{course.videoMinutes} {t('courses:flex.mins')}
								<br />
								{t('courses:flex.online_videos')}
							</p>
						</Icon>
						<Icon>
							<span>
								<FaChalkboardTeacher />
							</span>
							<p>
								{course.inPersonHours} {t('courses:flex.hrs')}
								<br />
								{t('courses:flex.in_person_class')}
							</p>
						</Icon>
						<Icon>
							<span>
								<FaCode />
							</span>
							<p>
								{course.caseCodes} {t('courses:flex.pcs')}
								<br />
								{t('courses:flex.code_snippets')}
							</p>
						</Icon>
					</div>
					<div>
						<MdLanguage />{' '}
						{t(
							'courses:flex.cantonese_lessons_with_english_materials'
						)}
					</div>
				</div>
			</div>
			<FlexContainer>
				<div>
					<h3>{t('courses:flex.upcoming_online_videos_schedule')}</h3>
					<FlexSchedule>
						<div>
							<div className='content'>
								<span>
									<FaVideo /> + <FaCode />
								</span>
								<p className='month'>
									{t('courses:flex.self_paced_learning')}
								</p>
								<p className='day'>
									{t('courses:flex.access_via_online_system')}
								</p>
								<p className='location'>
									{t('courses:flex.any_date_time_and_venue')}
								</p>
								<p className='time'>
									{t(
										'courses:flex.unlimited_life_long_access'
									)}
								</p>
							</div>
							<div className='price'>
								<del>
									<span className='currency'>{t('hkd')}</span>
									{course.price_video_standard_price}
								</del>
								<p>
									<span className='currency'>{t('hkd')}</span>
									{course.price_video}
								</p>
								<em>
									{t('per_person')}
								</em>
								<ButtonA
									href={
										'https://wa.me/85297256400?text=' +
										encodeURIComponent(
											t('courses:flex.wish_apply_video', {
												course: course.name,
											}) + ''
										)
									}>
									{t('courses:apply_now')}
								</ButtonA>
							</div>
						</div>
					</FlexSchedule>
					<h4>
						{t('courses:flex.upcoming_in_person_class_schedule')}
					</h4>
					<FlexSchedule>
						{cohorts
							.filter((cohort) => cohort.show)
							.map((cohort) => (
								<div key={cohort.name}>
									<div className='content'>
										<span>
											<FaVideo /> +{' '}
											<FaChalkboardTeacher /> + <FaCode />
										</span>
										<p className='month'>
											{t(cohort.name)}
										</p>
										<p className='day'>
											{cohort.dates.join(', ')}
										</p>
										<p className='location'>
											{t('courses:flex.venue')}:{' '}
											{cohort.venue}
										</p>
										<p className='time'>
											{cohort.startTime} -{' '}
											{cohort.endTime}
										</p>
									</div>
									<div className='price'>
										<del>
											<span className='currency'>
												{t('hkd')}
											</span>
											{
												course.price_in_person_standard_price
											}
										</del>{' '}
										<p>
											<span className='currency'>
												{t('hkd')}
											</span>
											{course.price_in_person}
										</p>
										<em>
											{t('per_person')}
										</em>
										<ButtonA
											href={
												'https://wa.me/85297256400?text=' +
												encodeURIComponent(
													t(
														'courses:flex.wish_apply_in_person',
														{
															course: course.name,
															month: cohort.name,
														}
													) + ''
												)
											}>
											{t('courses:apply_now')}
										</ButtonA>
									</div>
								</div>
							))}
						<div>
							<div className='content'>
								<span>
									<FaVideo /> + <FaChalkboardTeacher /> +{' '}
									<FaCode />
								</span>
								<p className='month'>
									{t('courses:flex.custom_class')}
								</p>
								<p className='day'>
									{t('courses:flex.custom_date_and_time')}
								</p>
								<p className='location'>
									{t('courses:flex.custom_venue')}
								</p>
								<p className='time'>
									{t('courses:flex.total')}&nbsp;
									{course.inPersonHours}&nbsp;
									{t('courses:flex.hrs')}
								</p>
							</div>
							<div className='price'>
								<del>
									<span className='currency'>{t('hkd')}</span>
									{course.price_in_person_standard_price}
								</del>{' '}
								<p>
									<span className='currency'>{t('hkd')}</span>
									{course.price_in_person}
								</p>
								<ButtonA
									href={
										'https://wa.me/85297256400?text=' +
										encodeURIComponent(
											t(
												'courses:flex.wish_apply_custom_class',
												{
													course: course.name,
												}
											) + ''
										)
									}>
									{t('courses:apply_now')}
								</ButtonA>
							</div>
						</div>
					</FlexSchedule>
				</div>
				{/* <FlexButtonsFree course={course} /> */}
				<div
					css={css`
						padding-top: 10px !important;
						background: linear-gradient(
							rgba(255, 255, 255, 0.15),
							rgba(28, 150, 120, 0.15)
						);

						> div {
							display: flex;

							> * {
								flex: 1;
								display: flex;
								flex-direction: column;
								align-items: center;
								justify-content: center;
								margin: 40px 0;
							}

							p {
								padding: 0 10%;
							}

							.featureImage {
								text-align: center;

								img {
									max-width: 100%;
								}
							}

							${maxWidth[2]} {
								flex-direction: column;
							}
						}

						> div:first-of-type .featureImage {
							order: 1;

							${maxWidth[2]} {
								order: 0;
							}
						}
					`}>
					<h3>{course.blockTitle}</h3>
					{course.description?.map(
						(description, i) =>
							description && (
								<div key={i}>
									<div className='featureImage'>
										<img
											src={
												description.value?.image?.path!
											}
											alt={description.value?.title!}
										/>
									</div>
									<div>
										<h4>{description.value?.title}</h4>
										<p>{description.value?.subtitle}</p>
									</div>
								</div>
							)
					)}
				</div>
				{/* <FlexButtonsFree course={course} /> */}
				<div
					css={css`
						padding: 40px 20%;
						background: linear-gradient(
							rgba(5, 58, 135, 0.15),
							rgba(255, 255, 255, 0.15)
						);

						${maxWidth[2]} {
							padding: 20px 10%;
						}
					`}>
					<h3
						css={css`
							line-height: 55px;
						`}>
						<span
							css={css`
								font-family: 'Kaushan Script', cursive;
							`}>
							Tecky Flex
						</span>{' '}
						{t('courses:flex.a_new_learning_experience')}
						<br />
						{t('courses:flex.online_offline_blended_learning')}
					</h3>
					<div
						css={css`
							> div {
								display: flex;

								.img {
									flex: 1;
									padding: 1rem;
								}
								.description {
									flex: 3;

									h3 {
										border: none;
									}
								}

								&:nth-child(even) {
									> .img {
										order: 1;
									}
								}

								${maxWidth[2]} {
									flex-direction: column;
								}
							}
						`}>
						<div>
							<div
								css={css`
									position: relative;
									flex: 1 1;
								`}>
								<GatsbyImage
									css={css`
										position: absolute;
										right: 0;
										top: 60px;
										width: 60%;
									`}
									image={
										data.screenshotAws?.childImageSharp
											?.gatsbyImageData
									}
									alt={t('courses:flex.online_learning')}
								/>
								<GatsbyImage
									css={css`
										position: relative;
										top: 70px;
										width: 60%;
									`}
									image={
										data.screenshotLesson?.childImageSharp
											?.gatsbyImageData
									}
									alt={t('courses:flex.online_learning')}
								/>
								<h4
									css={css`
										margin-top: 100px !important;
									`}>
									{t('courses:flex.online_learning')}
								</h4>
							</div>
							<div
								css={css`
									flex: 0 1;
									padding: 20px;
									font-size: 40px;
									display: flex;
									align-items: center;
									justify-content: center;
								`}>
								+
							</div>
							<div
								css={css`
									position: relative;
									flex: 1 1;
								`}>
								<GatsbyImage
									css={css`
										position: absolute;
										right: 0;
										top: 60px;
										width: 60%;
									`}
									image={
										data.photoTeach?.childImageSharp
											?.gatsbyImageData
									}
									alt={t('courses:flex.in_person_learning')}
								/>
								<GatsbyImage
									css={css`
										position: relative;
										top: 70px;
										width: 60%;
									`}
									image={
										data.photoLesson?.childImageSharp
											?.gatsbyImageData
									}
									alt={t('courses:flex.in_person_learning')}
								/>
								<h4
									css={css`
										margin-top: 100px !important;
									`}>
									{t('courses:flex.in_person_learning')}
								</h4>
							</div>
						</div>
					</div>
					<div>
						<div>
							<h5>
								{t(
									'courses:flex.disruptive_learning_model.title'
								)}
							</h5>
							<p>
								{t(
									'courses:flex.disruptive_learning_model.description'
								)}
							</p>
						</div>
					</div>
					<div>
						<div>
							<h5>{t('courses:flex.lifelong_access.title')}</h5>
							<p>
								{t('courses:flex.lifelong_access.description')}
							</p>
						</div>
					</div>
				</div>
				<div>
					<h4>{t('courses:flex.course_content_includes')}</h4>
					<div
						css={css`
							display: flex;
							color: #ffffff;

							${maxWidth[2]} {
								flex-direction: column;
							}
						`}>
						<FlexSection>
							<div>
								<span>
									<FaVideo />
								</span>
								<p>
									{course.videoMinutes}{' '}
									{t('courses:flex.mins')}
									<br />
									{t('courses:flex.online_videos')}
								</p>
							</div>
							{course.videos?.map(
								(video, i) =>
									video && (
										<div className='video' key={i}>
											{i + 1}. {video.value?.title}
											<span>({video.value?.length})</span>
										</div>
									)
							)}
						</FlexSection>
						<FlexSection>
							<div>
								<span>
									<FaChalkboardTeacher />
								</span>
								<p>
									{course.inPersonHours}{' '}
									{t('courses:flex.hrs')}
									<br />
									{t('courses:flex.in_person_class')}
								</p>
							</div>
							{course.lessons?.map(
								(lesson, i) =>
									lesson && (
										<div className='practice' key={i}>
											<p>
												<strong>
													{t(
														'courses:flex.in_person_lesson'
													)}{' '}
													{i + 1}
												</strong>
											</p>
											<p>
												{lesson.value?.length}{' '}
												{t('courses:flex.hrs')} |{' '}
												{t('courses:flex.cantonese')} |{' '}
												{t('courses:flex.practical')}
											</p>
											<p>{lesson.value?.description}</p>
										</div>
									)
							)}
						</FlexSection>
						<FlexSection>
							<div>
								<span>
									<FaCode />
								</span>
								<p>
									{course.caseCodes} {t('courses:flex.pcs')}
									<br />
									{t('courses:flex.code_snippets')}
								</p>
							</div>
							{course.cases?.split('\n').map((caseTitle, i) => (
								<div className='codes' key={i}>
									{caseTitle}
								</div>
							))}
						</FlexSection>
					</div>
				</div>
				{/* <FlexButtonsFree course={course} /> */}
				{(course.samples?.length ?? 0) > 0 && (
					<div>
						<h4>{t('courses:flex.sample_video_previews')}</h4>
						<div
							css={css`
								display: flex;
								justify-content: center;
								align-items: center;
								flex-direction: column;
								width: 100%;

								iframe {
									width: 100%;
									margin: 10px;
									max-width: 650px;
								}

								button {
									display: flex;
									margin: 10px;
									border: 0;
									background-color: #ff9900;
									color: #ffffff;
									width: 180px;
									height: 30px;
									font-size: 16px;
									border-radius: 15px;
									justify-content: center;
									align-items: center;
									position: relative;

									svg {
										position: absolute;
										top: 7px;
										left: 7px;
									}
								}
							`}>
							{course.samples?.map(
								(video, i) =>
									video && (
										<React.Fragment key={i}>
											<a href='#'>
												<FaPlayCircle />
												{video.value?.title}
											</a>
											<iframe
												height='315'
												src={
													'https://www.youtube.com/embed/' +
													video.value?.youtubeLink
												}
												frameBorder='0'
												title={video.value?.title!}
												allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
												allowFullScreen></iframe>
										</React.Fragment>
									)
							)}
						</div>
					</div>
				)}
			<h4>
				{t('courses:common.course_cert')}
			</h4>
				<div
							css={css`
								justify-content: center;
								align-item: center;
								margin-left: 20px;
								width: 80%;
								height: 80%;
							`}>
							<GatsbyImage
									image={
										course.certificateSharp?.childImageSharp
										?.gatsbyImageData
									}
									alt={course.certificate?.meta?.title}
									/>
							</div>
									<div>
										{t('courses:common.course_cert_description')}
									</div>
				<div>
					<h4>{t('courses:flex.faqs')}</h4>
					<div
						css={css`
							details {
								JavaScript Fundamentalsmargin: 20px;
								background: rgba(255, 255, 255, 0.1);
								padding: 15px 10px 10px;

								summary {
									margin-bottom: 10px;
									color: #ffcc00;
									font-size: 24px;
									cursor: pointer;
								}
								summary::-webkit-details-marker {
									display: none;
								}
							}
						`}>
						{data.faqs.nodes
							.filter((faq) => faq.Type === 'Course')
							.map((faq, i) => (
								<details key={i}>
									<summary>{faq.Question}</summary>
									{faq.Answer}
								</details>
							))}
					</div>
				</div>
				<div>
					<h4>{t('courses:flex.your_instructors')}</h4>
					<StaffWrap>
						{data.staff.nodes.map((staff, i) => (
							<StaffComponent staff={staff} key={i} />
						))}
					</StaffWrap>
				</div>
				<div>
					<h4>{t('courses:flex.still_have_questions')}</h4>
					<a
						css={css`
							text-align: center;
							background: ${theme.buttonColor};
							color: #ffffff;
							border-radius: 25px;
							padding: 20px;
							display: block;
							font-size: 24px;
						`}
						href={
							'https://wa.me/85297256400?text=' +
							encodeURIComponent(
								t('courses:flex.wish_to_lean_more_whatsapp', {
									course: course.name,
								}) + ''
							)
						}>
						{t('courses:flex.send_us_a_message_to_know_more')}
						<br />
						<FaWhatsapp /> 9725-6400
					</a>
				</div>
			</FlexContainer>
		</Container>
	)
}

export default FlexIndex

export const query = graphql`
	query FlexIndex($language: String!, $id: String!, $regex: String!) {
		...TranslationFragment
		events: allGoogleCalendarEvent(filter: { summary: { regex: $regex } }) {
			edges {
				node {
					id
					summary
					status
					start {
						dateTime
					}
					end {
						dateTime
					}
					location
				}
			}
		}
		course: cockpitFlexCoursesNewCollection(
			_lang: { eq: $language }
			_id: { eq: $id }
		) {
			id
			code
			name
			tagline
			iconSharp {
				childImageSharp {
					gatsbyImageData(width: 400, height: 400)
				}
			}
			videoMinutes
			inPersonHours
			caseCodes
			blockTitle
			description {
				value {
					title
					subtitle
					image {
						path
					}
				}
			}
			lessons {
				value {
					length
					description
				}
			}
			cases
			freeVideos
			videos {
				value {
					title
					length
				}
			}
			samples {
				value {
					title
					youtubeLink
				}
			}
			certificate {
				path
				meta
			  }
			  certificateSharp {
				childImageSharp {
				  gatsbyImageData
				}
			  }
			price_video_standard_price
			price_video
			price_in_person_standard_price
			price_in_person
		}
		faqs: allCockpitFaqfapCollection(filter: { _lang: { eq: $language } }) {
			nodes {
				Question
				Answer
				Type
			}
		}
		screenshotAws: file(
			relativePath: { eq: "flex-icons/screenshot-aws.png" }
		) {
			childImageSharp {
				gatsbyImageData(width: 400)
			}
		}
		screenshotLesson: file(
			relativePath: { eq: "flex-icons/screenshot-lesson.png" }
		) {
			childImageSharp {
				gatsbyImageData(width: 400)
			}
		}
		photoTeach: file(relativePath: { eq: "flex-icons/photo-teach.jpg" }) {
			childImageSharp {
				gatsbyImageData(width: 400)
			}
		}
		photoLesson: file(relativePath: { eq: "flex-icons/photo-lesson.jpg" }) {
			childImageSharp {
				gatsbyImageData(width: 400)
			}
		}
		marker: file(relativePath: { eq: "map_marker.png" }) {
			childImageSharp {
				fluid(maxWidth: 50) {
					src
				}
			}
		}
		staff: allCockpitStaffCollection(
			filter: {
				active: { eq: true }
				flex: { eq: true }
				_lang: { eq: $language }
			}
			sort: { fields: _o, order: ASC }
		) {
			nodes {
				_id
				name
				description
				title
				specialities
				profileImg {
					path
				}
				available_countries
				profileImgSharp {
					childImageSharp {
						gatsbyImageData(
							width: 250
							height: 250
							placeholder: BLURRED
						)
					}
				}
			}
		}
		locations: allCockpitLocationsCollection(
			filter: { _lang: { eq: $language } }
			sort: { fields: _o, order: ASC }
		) {
			nodes {
				_id
				address1title
				address1b
				address1a
				address2b
				address2a
				name
				gallery {
					path
				}
				location {
					lat
					lng
				}
			}
		}
	}
`
